import { useSyncExternalStore } from 'react';

/** Mirrors `@media (max-width: 640px)` in the CSS (see tokens.css). */
export const MOBILE_QUERY = '(max-width: 640px)';
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function matches(query: string): boolean {
  // jsdom has no matchMedia: behave like a desktop without reduced motion.
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

/** Live `matchMedia` value. Use only where the *composition* changes; style with CSS otherwise. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== 'function') return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => {
        list.removeEventListener('change', onChange);
      };
    },
    () => matches(query),
    () => false,
  );
}

/**
 * A touch-first interface: a phone-width layout or a coarse primary pointer. Pickers use it NOT to
 * move focus into their search field when they open (that would raise the on-screen keyboard
 * before the person asked to type); the field is still there and a tap on it focuses it.
 */
export const TOUCH_UI_QUERY = `${MOBILE_QUERY}, (pointer: coarse)`;

export function useIsTouchUi(): boolean {
  return useMediaQuery(TOUCH_UI_QUERY);
}

export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}

/**
 * A phone turned sideways (or any window as short): wide enough for the desktop composition but
 * with too little HEIGHT for stacked bars. Mirrors the CSS block of the same condition in
 * tokens.css; width alone cannot tell this case from a desktop.
 */
export const SHORT_LANDSCAPE_QUERY =
  '(orientation: landscape) and (max-height: 500px) and (max-width: 960px)';

export function useIsShortLandscape(): boolean {
  return useMediaQuery(SHORT_LANDSCAPE_QUERY);
}

export function prefersReducedMotion(): boolean {
  return matches(REDUCED_MOTION_QUERY);
}

/** The primary input is touch (no hover, coarse pointer): keyboard-only microcopy is noise there. */
export const TOUCH_PRIMARY_QUERY = '(hover: none) and (pointer: coarse)';

export function useTouchPrimary(): boolean {
  return useMediaQuery(TOUCH_PRIMARY_QUERY);
}
